import { useCallback, useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { broadcastAdminDataUpdated, useAdminDataRefresh } from "@/lib/admin-refresh";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, MessageCircle, CalendarClock, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import {
  listUpcomingInterviews,
  updateInterviewStatus,
  updateInterviewDate,
  type UpcomingInterview,
  type InterviewStatus,
} from "@/lib/admin-interviews.functions";
import { listPaidStudentsLite as listPaidStudents, type PaidStudent } from "@/lib/admin-students.functions";
import { waDigits } from "@/lib/phone";

const STATUS_BADGE: Record<InterviewStatus, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-slate-100 text-slate-700" },
  approved: { label: "Approved", cls: "bg-emerald-100 text-emerald-800" },
  refused: { label: "Refused", cls: "bg-rose-100 text-rose-800" },
  admin_processing: { label: "221(g)", cls: "bg-amber-100 text-amber-800" },
};

function followUpUrl(name: string, phone: string) {
  const digits = waDigits(phone);
  if (!digits) return "";
  return `https://wa.me/${digits}?text=Hi%20${encodeURIComponent(name || "there")},%20how%20did%20your%20visa%20interview%20go%20today?`;
}

function askDateUrl(name: string, phone: string) {
  const digits = waDigits(phone);
  if (!digits) return "";
  return `https://wa.me/${digits}?text=Hi%20${encodeURIComponent(
    name || "there",
  )},%20have%20you%20booked%20your%20visa%20interview%20date%20yet?%20Please%20let%20us%20know%20so%20we%20can%20schedule%20your%20prep%20sessions.`;
}

/**
 * Merge entries that share ANY identity signal (name, phone digits, or email)
 * so each real student shows up only once.
 */
function dedupeByIdentity<T>(
  items: T[],
  pick: (item: T) => { name?: string | null; phone?: string | null; email?: string | null },
): T[] {
  const seen = new Map<string, number>(); // identity token -> group index
  const groups: T[] = [];

  for (const item of items) {
    const id = pick(item);
    const tokens = [
      id.email ? `e:${id.email.toLowerCase().trim()}` : "",
      id.phone ? `p:${waDigits(id.phone).slice(-10)}` : "",
      id.name ? `n:${id.name.toLowerCase().replace(/\s+/g, " ").trim()}` : "",
    ].filter((t) => t.length > 2);

    const hit = tokens.map((t) => seen.get(t)).find((v) => v !== undefined);
    if (hit !== undefined) {
      tokens.forEach((t) => seen.set(t, hit));
      continue; // duplicate — keep the first (most recent) record
    }
    const index = groups.push(item) - 1;
    tokens.forEach((t) => seen.set(t, index));
  }
  return groups;
}


export function EverydayInterviewTracker({ onChange }: { onChange?: () => void }) {
  const fetchInterviews = useServerFn(listUpcomingInterviews);
  const fetchPaid = useServerFn(listPaidStudents);
  const saveStatus = useServerFn(updateInterviewStatus);
  const saveDate = useServerFn(updateInterviewDate);

  const [tab, setTab] = useState<"today" | "unscheduled">("today");
  const [unscheduledFilter, setUnscheduledFilter] = useState<"all" | "approved" | "refused">("all");
  const [loading, setLoading] = useState(true);
  const [interviews, setInterviews] = useState<UpcomingInterview[]>([]);
  const [paid, setPaid] = useState<PaidStudent[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [dateDrafts, setDateDrafts] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const [a, b] = await Promise.all([fetchInterviews(), fetchPaid()]);
      setInterviews(a?.interviews ?? []);
      setPaid(b?.students ?? []);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load tracker");
    } finally {
      setLoading(false);
    }
  }, [fetchInterviews, fetchPaid]);

  useEffect(() => {
    void load();
  }, [load]);

  // Re-fetch whenever any admin surface edits student/payment records.
  useAdminDataRefresh(load);

  const todayKey = new Date().toISOString().split("T")[0];

  const todays = useMemo(
    () =>
      dedupeByIdentity(
        interviews.filter((i) => (i.interviewDate || "").slice(0, 10) === todayKey),
        (i) => ({ name: i.name, phone: i.phone, email: i.email }),
      ),
    [interviews, todayKey],
  );
  const pendingToday = todays.filter((i) => (i.interviewStatus ?? "pending") === "pending").length;

  const uniquePaid = useMemo(
    () => dedupeByIdentity(paid, (s) => ({ name: s.name, phone: s.phone, email: s.email })),
    [paid],
  );
  const unscheduled = useMemo(() => uniquePaid.filter((s) => !s.interview_date), [uniquePaid]);
  const totalPaid = uniquePaid.length;

  const approvedUnscheduled = useMemo(
    () => unscheduled.filter((s) => (s.interview_status ?? "pending") === "approved"),
    [unscheduled],
  );
  const refusedUnscheduled = useMemo(
    () => unscheduled.filter((s) => (s.interview_status ?? "pending") === "refused"),
    [unscheduled],
  );
  const visibleUnscheduled = useMemo(() => {
    if (unscheduledFilter === "approved") return approvedUnscheduled;
    if (unscheduledFilter === "refused") return refusedUnscheduled;
    return unscheduled;
  }, [unscheduledFilter, approvedUnscheduled, refusedUnscheduled, unscheduled]);

  // A student counts as tracked once they have a date OR a recorded outcome.
  const decidedUnscheduled = approvedUnscheduled.length + refusedUnscheduled.length;
  const tracked = totalPaid - unscheduled.length + decidedUnscheduled;

  const setStatus = async (paymentId: string, status: InterviewStatus) => {
    const prevInterviews = interviews;
    const prevPaid = paid;
    setInterviews((xs) => xs.map((x) => (x.paymentId === paymentId ? { ...x, interviewStatus: status } : x)));
    setPaid((xs) => xs.map((x) => (x.payment_id === paymentId ? { ...x, interview_status: status } : x)));
    setSavingId(paymentId);
    try {
      await saveStatus({ data: { paymentId, status } });
      toast.success(`Marked ${STATUS_BADGE[status].label}`);
      onChange?.();
      // Push the change to every other admin widget (Upcoming Interviews, KPIs…)
      broadcastAdminDataUpdated();
    } catch (err) {
      setInterviews(prevInterviews);
      setPaid(prevPaid);
      toast.error(err instanceof Error ? err.message : "Failed to update");
    } finally {
      setSavingId(null);
    }
  };

  const setDate = async (paymentId: string) => {
    const value = dateDrafts[paymentId];
    if (!value) return;
    setSavingId(paymentId);
    try {
      await saveDate({ data: { paymentId, interviewDate: value } });
      setPaid((xs) => xs.map((x) => (x.payment_id === paymentId ? { ...x, interview_date: value } : x)));
      toast.success("Interview date saved");
      onChange?.();
      broadcastAdminDataUpdated();
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save date");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="rounded-xl border border-[#E2E8F0] bg-[#FAF8F5] p-4 shadow-sm sm:p-5 dark:border-zinc-800 dark:bg-zinc-950">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900 dark:text-white">
            <CalendarClock className="h-5 w-5 text-amber-500" />
            Everyday Interview Tracker &amp; WhatsApp Followup
          </h2>
          <p className="mt-0.5 text-xs font-semibold text-slate-500 dark:text-zinc-400">
            {format(new Date(), "EEEE, d MMMM yyyy")} · {todays.length} Scheduled Today · {pendingToday} Pending
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1.5">
          {tab === "unscheduled" && (
            <div className="flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] bg-white p-1 dark:border-zinc-800 dark:bg-zinc-900">
              <button
                type="button"
                onClick={() => setUnscheduledFilter("all")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  unscheduledFilter === "all"
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                All ({unscheduled.length})
              </button>
              <button
                type="button"
                onClick={() => setUnscheduledFilter(unscheduledFilter === "approved" ? "all" : "approved")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  unscheduledFilter === "approved"
                    ? "bg-emerald-600 text-white"
                    : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                }`}
              >
                Approved ({approvedUnscheduled.length})
              </button>
              <button
                type="button"
                onClick={() => setUnscheduledFilter(unscheduledFilter === "refused" ? "all" : "refused")}
                className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
                  unscheduledFilter === "refused"
                    ? "bg-rose-600 text-white"
                    : "bg-rose-100 text-rose-800 hover:bg-rose-200"
                }`}
              >
                Refused ({refusedUnscheduled.length})
              </button>
            </div>
          )}
          <div className="flex items-center gap-1.5 rounded-xl border border-[#E2E8F0] bg-white p-1 dark:border-zinc-800 dark:bg-zinc-900">
          <button
            type="button"
            onClick={() => setTab("today")}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              tab === "today" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-500"
            }`}
          >
            Today ({todays.length})
          </button>
          <button
            type="button"
            onClick={() => setTab("unscheduled")}
            className={`rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              tab === "unscheduled" ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900" : "text-slate-500"
            }`}
          >
            Unscheduled ({unscheduled.length})
          </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="grid place-items-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : tab === "today" ? (
        todays.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-500">No interviews scheduled for today.</p>
        ) : (
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[640px] border-separate border-spacing-y-2">
              <tbody>
                {todays.map((i) => {
                  const badge = STATUS_BADGE[i.interviewStatus ?? "pending"] ?? STATUS_BADGE.pending;
                  const wa = followUpUrl(i.name, i.phone);
                  return (
                    <tr key={i.paymentId} className="rounded-xl bg-white shadow-sm dark:bg-zinc-900">
                      <td className="rounded-l-xl border-y border-l border-[#E2E8F0] px-4 py-3 dark:border-zinc-800">
                        <p className="text-sm font-bold text-slate-900 dark:text-white">{i.name || "Student"}</p>
                        <div className="mt-1 flex flex-wrap items-center gap-1.5">
                          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-zinc-800 dark:text-zinc-300">
                            {i.plan}
                          </span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${badge.cls}`}>
                            {badge.label}
                          </span>
                        </div>
                      </td>
                      <td className="border-y border-[#E2E8F0] px-3 py-3 dark:border-zinc-800">
                        {wa ? (
                          <a
                            href={wa}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-600"
                          >
                            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                          </a>
                        ) : (
                          <span className="text-[11px] text-slate-400">No phone</span>
                        )}
                      </td>
                      <td className="rounded-r-xl border-y border-r border-[#E2E8F0] px-4 py-3 dark:border-zinc-800">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          <button
                            type="button"
                            disabled={savingId === i.paymentId}
                            onClick={() => void setStatus(i.paymentId, "approved")}
                            className="inline-flex items-center gap-1 rounded-lg bg-emerald-100 px-2.5 py-1.5 text-[11px] font-bold text-emerald-800 transition hover:bg-emerald-200 disabled:opacity-50"
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" /> Approved
                          </button>
                          <button
                            type="button"
                            disabled={savingId === i.paymentId}
                            onClick={() => void setStatus(i.paymentId, "refused")}
                            className="inline-flex items-center gap-1 rounded-lg bg-rose-100 px-2.5 py-1.5 text-[11px] font-bold text-rose-800 transition hover:bg-rose-200 disabled:opacity-50"
                          >
                            <XCircle className="h-3.5 w-3.5" /> Refused
                          </button>
                          {savingId === i.paymentId && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      ) : (
        <div className="mt-4">
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-bold text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {visibleUnscheduled.length} {unscheduledFilter === "all" ? "Paid Students Missing Interview Dates" : unscheduledFilter === "approved" ? "Approved Students (No Date)" : "Refused / 214(b) Students (No Date)"} | Total Paid: {totalPaid} · Tracked: {tracked}
            </span>
          </div>

          {visibleUnscheduled.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-500">
              {unscheduledFilter === "all"
                ? "Every paid student has an interview date. 🎉"
                : `No ${unscheduledFilter} students missing interview dates.`}
            </p>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full min-w-[640px] border-separate border-spacing-y-2">
                <tbody>
                  {visibleUnscheduled.map((s) => {
                    const wa = askDateUrl(s.name, s.phone ?? "");
                    return (
                      <tr key={s.payment_id} className="bg-white dark:bg-zinc-900">
                        <td className="rounded-l-xl border-y border-l border-[#E2E8F0] px-4 py-3 dark:border-zinc-800">
                          <p className="text-sm font-bold text-slate-900 dark:text-white">{s.name}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span className="inline-block rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 dark:bg-zinc-800 dark:text-zinc-300">
                              {s.plan_name}
                            </span>
                            <span
                              className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-bold ${
                                (STATUS_BADGE[(s.interview_status ?? "pending") as InterviewStatus] ?? STATUS_BADGE.pending).cls
                              }`}
                            >
                              {(STATUS_BADGE[(s.interview_status ?? "pending") as InterviewStatus] ?? STATUS_BADGE.pending).label}
                            </span>
                          </div>
                        </td>
                        <td className="border-y border-[#E2E8F0] px-3 py-3 dark:border-zinc-800">
                          {wa ? (
                            <a
                              href={wa}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-emerald-600"
                            >
                              <MessageCircle className="h-3.5 w-3.5" /> Ask via WhatsApp
                            </a>
                          ) : (
                            <span className="text-[11px] text-slate-400">No phone</span>
                          )}
                        </td>
                        <td className="rounded-r-xl border-y border-r border-[#E2E8F0] px-4 py-3 dark:border-zinc-800">
                          <div className="flex flex-wrap items-center justify-end gap-2">
                            <button
                              type="button"
                              disabled={savingId === s.payment_id}
                              onClick={() => void setStatus(s.payment_id, "approved")}
                              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[11px] font-bold transition disabled:opacity-50 ${
                                (s.interview_status ?? "pending") === "approved"
                                  ? "border-emerald-600 bg-emerald-600 text-white"
                                  : "border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                              }`}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" /> Approved
                            </button>
                            <button
                              type="button"
                              disabled={savingId === s.payment_id}
                              onClick={() => void setStatus(s.payment_id, "refused")}
                              className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1.5 text-[11px] font-bold transition disabled:opacity-50 ${
                                (s.interview_status ?? "pending") === "refused"
                                  ? "border-rose-600 bg-rose-600 text-white"
                                  : "border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100"
                              }`}
                            >
                              <XCircle className="h-3.5 w-3.5" /> Refused
                            </button>
                            <input
                              type="date"
                              value={dateDrafts[s.payment_id] ?? ""}
                              onChange={(e) => setDateDrafts((d) => ({ ...d, [s.payment_id]: e.target.value }))}
                              className="rounded-lg border border-[#E2E8F0] bg-white px-2 py-1.5 text-xs dark:border-zinc-800 dark:bg-zinc-950"
                            />
                            <button
                              type="button"
                              disabled={!dateDrafts[s.payment_id] || savingId === s.payment_id}
                              onClick={() => void setDate(s.payment_id)}
                              className="inline-flex items-center gap-1 rounded-lg bg-slate-900 px-3 py-1.5 text-[11px] font-bold text-white transition hover:bg-slate-800 disabled:opacity-40 dark:bg-white dark:text-slate-900"
                            >
                              {savingId === s.payment_id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                "Set Date"
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
